import { VERIFICATION_ACCOUNT_TYPE } from '@app/domain/accountVerification';
import { SergeeSearchResult } from '@mortee/domain/morteeAccountVerification';
import { UnifiedPayee } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import AccountVerificationInitialMissingFieldsPageV2 from './AccountVerificationInitialMissingFieldsPageV2';
import AccountVerificationInitialPageV2 from './AccountVerificationInitialPageV2';
import AccountVerificationQuickCheckResultPage from './AccountVerificationQuickCheckResultPage';
import { AccountVerificationForm, handleAccountVerificationSubmit } from './searchPayeeUtils';
import { ResultPageState, ResultPageActions, handleCancelKnoxValidation, handleApprove } from './resultPageUtils';

export default function AccountVerificationInitialPageV2Container(): JSX.Element {
  const initialFormData: AccountVerificationForm & { countryCode: string } = {
    accountNumber: '',
    accountType: VERIFICATION_ACCOUNT_TYPE.domestic,
    country: '',
    iban: '',
    knoxOrTaxId: '',
    payeeName: '',
    bankCode: '',
    branchCode: '',
    swiftBic: '',
    taxId: '',
    countryCode: '',
  };

  const [showResult, setShowResult] = useState<boolean>(false);
  const [showMissingFields, setShowMissingFields] = useState<boolean>(false);
  const [missingFields, setMissingFields] = useState<string[]>(['bankCode', 'branchCode']);
  const [formDataPersistent, setFormDataPersistent] = useState<AccountVerificationForm>(initialFormData);
  const [selectedPayee, setSelectedPayee] = useState<UnifiedPayee | null>(null);
  const [verificationResult, setVerificationResult] = useState<SergeeSearchResult | null>(null);
  const [, setVerificationError] = useState<Error | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const isInitialSelection = useRef(true);
  const [countryCode, setCountryCode] = useState<string>('');

  // Result page state
  const [resultPageState, setResultPageState] = useState<ResultPageState>({
    isApproving: false,
    isApproved: false,
    showMatchingDialog: false,
    matchingResults: null,
  });

  const resultPageActions: ResultPageActions = {
    setIsApproving: (isApproving: boolean) => setResultPageState((prev) => ({ ...prev, isApproving })),
    setIsApproved: (isApproved: boolean) => setResultPageState((prev) => ({ ...prev, isApproved })),
    setShowMatchingDialog: (showMatchingDialog: boolean) => setResultPageState((prev) => ({ ...prev, showMatchingDialog })),
    setMatchingResults: (matchingResults) => setResultPageState((prev) => ({ ...prev, matchingResults })),
  };

  useEffect(() => {
    if (selectedPayee) {
      const payeeName = selectedPayee.names?.[0]?.name || '';
      const knoxId = selectedPayee.strongLegalIdentifies?.KnoxId?.[0]?.value || '';
      const taxId = selectedPayee.strongLegalIdentifies?.TaxId?.[0]?.value || '';
      const knoxOrTaxId = knoxId || taxId;

      if (isInitialSelection.current) {
        isInitialSelection.current = false;
        return;
      }

      if (formDataPersistent.payeeName !== payeeName || formDataPersistent.knoxOrTaxId !== knoxOrTaxId) {
        setSelectedPayee(null);
      }
    } else {
      isInitialSelection.current = true;
    }
  }, [formDataPersistent.payeeName, formDataPersistent.knoxOrTaxId, selectedPayee]);

  const handleResultPageBack = (toastmessage: string, wasApproved?: boolean): void => {
    setShowResult(false);

    if (wasApproved) {
      toast.success(toastmessage, {
        duration: 5000,
        position: 'top-right',
        style: { whiteSpace: 'pre-wrap' },
      });
    }

    // Always clear form values when going back
    setFormDataPersistent(initialFormData);
    setSelectedPayee(null);
    setCountryCode('');
    setVerificationResult(null);
    setVerificationError(null);

    // Reset result page state
    setResultPageState({
      isApproving: false,
      isApproved: false,
      showMatchingDialog: false,
      matchingResults: null,
    });
  };

  const handleResultPageApprove = async (): Promise<void> => {
    await handleApprove(verificationResult, resultPageActions, handleResultPageBack);
  };

  const handleResultPageCancelKnoxValidation = async (): Promise<void> => {
    await handleCancelKnoxValidation(
      verificationResult,
      resultPageState.matchingResults,
      resultPageActions.setShowMatchingDialog,
      handleResultPageBack,
    );
  };

  const handleResultPageContinueKnoxValidation = (): void => {
    resultPageActions.setShowMatchingDialog(false);
    handleResultPageBack('Knox verify will continue.', true);
  };

  if (showResult && formDataPersistent) {
    return (
      <AccountVerificationQuickCheckResultPage
        formData={formDataPersistent}
        verificationResult={verificationResult}
        isVerifying={isVerifying}
        resultPageState={resultPageState}
        onBack={handleResultPageBack}
        onApprove={handleResultPageApprove}
        onCancelKnoxValidation={handleResultPageCancelKnoxValidation}
        onContinueKnoxValidation={handleResultPageContinueKnoxValidation}
      />
    );
  }

  if (showMissingFields && formDataPersistent) {
    return (
      <AccountVerificationInitialMissingFieldsPageV2
        formData={formDataPersistent}
        missingFields={missingFields}
        setCountryCode={setCountryCode}
        verificationResult={verificationResult}
        onSubmit={async (currentFormData: AccountVerificationForm): Promise<void> => {
          await handleAccountVerificationSubmit({
            currentFormData,
            selectedPayee,
            countryCode,
            setVerificationError,
            setShowResult,
            setIsVerifying,
            setVerificationResult,
            setFormDataPersistent,
            setMissingFields,
            setShowMissingFields,
          });
        }}
        onBack={(): void => setShowMissingFields(false)}
      />
    );
  }

  return (
    <AccountVerificationInitialPageV2
      showResult={setShowResult}
      persistedData={formDataPersistent}
      setSelectedPayee={setSelectedPayee}
      setCountryCode={setCountryCode}
      onSubmitForm={async (currentFormData: AccountVerificationForm): Promise<void> => {
        await handleAccountVerificationSubmit({
          currentFormData,
          selectedPayee,
          countryCode,
          setVerificationError,
          setShowResult,
          setIsVerifying,
          setVerificationResult,
          setFormDataPersistent,
          setMissingFields,
          setShowMissingFields,
        });
      }}
    />
  );
}
